import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, FileText, Loader2, AlertCircle, CheckCircle, Check, PiggyBank } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal de paiement Body-Up.
 *
 * UX :
 *  - Step 1 : Choix méthode (Stripe carte ou Virement QR-bill)
 *      + Toggle Mensuel/Paiement intégral (-5%) pour coaching online/hybride
 *  - Step 2 (Stripe) : Choix 1x / 2x +3% / 3x +5% (1x = +3% aussi, sauf Coaching Hybride/100% en ligne = 0%)
 *  - Step 2 (Virement) : Formulaire client + génération facture QR-bill
 *  - Step 3 : Succès virement (facture téléchargée)
 *
 * Props : { isOpen, onClose, item: { name, price, total, type, maxInstallments, allowAnnual, months } }
 */
export const PaymentModal = ({ isOpen, onClose, item }) => {
  const [step, setStep] = useState(1);
  const [method, setMethod] = useState(null);            // 'stripe' | 'bank_transfer'
  const [installments, setInstallments] = useState(1);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | 'annual' (=paiement intégral)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [customerInfo, setCustomerInfo] = useState({ name: '', email: '', address: '', city: '' });
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null); // { code, discounted_amount, discount_type, discount_value }
  const [promoError, setPromoError] = useState('');
  const [promoChecking, setPromoChecking] = useState(false);

  const allowAnnual = !!(item && item.allowAnnual);
  const isAnnual = allowAnnual && billingCycle === 'annual';

  // Quand annuel est activé, on impose 1x
  useEffect(() => {
    if (isAnnual && installments !== 1) setInstallments(1);
  }, [isAnnual, installments]);

  // Reset complet quand on ferme le modal
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setMethod(null);
      setInstallments(1);
      setBillingCycle('monthly');
      setError('');
      setCustomerInfo({ name: '', email: '', address: '', city: '' });
      setPromoCode(''); setPromoApplied(null); setPromoError('');
    }
  }, [isOpen]);

  if (!item) return null;

  // --- Calculs prix ---
  const monthlyPrice = typeof item.price === 'number' ? item.price : parseFloat(item.price);
  const totalEngagement = typeof item.total === 'number' ? item.total : parseFloat(item.total || item.price);
  const basePriceNum = isAnnual
    ? totalEngagement * 0.95
    : (allowAnnual ? totalEngagement : monthlyPrice * (item.months || 1));

  // Frais Stripe : les forfaits Coaching Hybride / 100% en ligne restent à 0% en 1x
  // (leur engagement long justifie l'absence de frais). Tous les autres forfaits
  // (Personal Training, EMS, Scan 3D, etc.) ont désormais +3% en 1x pour couvrir les
  // frais bancaires Stripe. Le virement bancaire reste toujours à 0% de frais.
  const cardFeeExempt = item.type === 'coaching-hybride' || item.type === 'coaching-online';
  const feesByInstallment = (n) => (n === 1 ? (cardFeeExempt ? 0 : 0.03) : n === 2 ? 0.03 : n === 3 ? 0.05 : 0);
  const feeRate = feesByInstallment(installments);
  const effectiveBasePriceNum = promoApplied ? promoApplied.discounted_amount : basePriceNum;
  const totalWithFees = effectiveBasePriceNum * (1 + feeRate);
  const monthlyAmount = totalWithFees / installments;
  const priceNum = effectiveBasePriceNum;

  const checkPromoCode = async () => {
    if (!promoCode.trim()) return;
    setPromoChecking(true); setPromoError(''); setPromoApplied(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/payment/promo-preview?code=${encodeURIComponent(promoCode.trim())}&amount=${basePriceNum}`);
      const data = await res.json();
      if (!res.ok) { setPromoError(data.detail || 'Code invalide.'); return; }
      setPromoApplied(data);
    } catch {
      setPromoError('Erreur réseau, réessayez.');
    } finally {
      setPromoChecking(false);
    }
  };

  // Options d'échelonnement disponibles
  const installmentOptions = (() => {
    if (isAnnual) return [1];
    const max = item.maxInstallments || 1;
    return [1, 2, 3].filter((n) => n <= max);
  })();

  // --- Handlers paiement ---
  const handleStripePayment = async () => {
    if (!customerInfo.name || !customerInfo.email) {
      setError('Veuillez renseigner votre nom et email.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/payment/create-stripe-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: basePriceNum,
          description: `${item.name}${isAnnual ? ' — Paiement intégral (-5%)' : ''}`,
          installments,
          customer_email: customerInfo.email,
          customer_name: customerInfo.name,
          product_type: item.type,
          annual: isAnnual,
          origin_url: window.location.origin,
          promo_code: promoApplied ? promoApplied.code : undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur création paiement');
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleBankTransfer = async () => {
    if (!customerInfo.name || !customerInfo.email) {
      setError('Veuillez renseigner votre nom et email.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/payment/generate-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: customerInfo.name,
          customer_email: customerInfo.email,
          customer_address: customerInfo.address,
          customer_city: customerInfo.city,
          items: [{ description: item.name, quantity: 1, unit_price: basePriceNum, total: basePriceNum }],
          subtotal: basePriceNum,
          total: basePriceNum,
          product_type: item.type,
          annual: isAnnual,
          notes: `Engagement sur la durée complète. Paiement par virement bancaire, sans frais.`,
        }),
      });
      if (!res.ok) throw new Error('Erreur génération facture');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Facture_Body-Up_${item.name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => { onClose(); };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200]"
            onClick={handleClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-[210] flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-zinc-900 to-black border border-white/10 rounded-3xl max-w-xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-r from-primary to-pink-500 px-6 py-4 flex items-center justify-between rounded-t-3xl z-10">
                <div className="min-w-0">
                  <h2 className="text-xl font-bold text-white truncate" data-testid="payment-modal-title">{item.name}</h2>
                  <p className="text-white/80 text-sm" data-testid="payment-modal-price">
                    {priceNum.toFixed(2)} CHF
                    {isAnnual && <span className="ml-2 px-2 py-0.5 bg-green-500/30 text-green-100 text-xs rounded-full">Intégral -5%</span>}
                  </p>
                </div>
                <button onClick={handleClose} className="p-2 hover:bg-white/20 rounded-full transition-colors" data-testid="payment-modal-close">
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>

              <div className="p-6">
                {/* === STEP 1 : Choix méthode (+ toggle annuel pour coaching) === */}
                {step === 1 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
                    {/* Toggle Mensuel / Paiement intégral (-5%) — coaching online/hybride uniquement */}
                    {allowAnnual && (
                      <div data-testid="billing-cycle-toggle">
                        <p className="text-zinc-400 text-xs uppercase tracking-wider mb-2">Formule</p>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button" onClick={() => setBillingCycle('monthly')}
                            className={`p-3 rounded-xl border-2 transition-all text-left ${billingCycle === 'monthly' ? 'border-primary bg-primary/15' : 'border-white/10 bg-white/5 hover:border-white/30'}`}
                            data-testid="cycle-monthly"
                          >
                            <div className="text-white font-semibold text-sm">Mensuel</div>
                            <div className="text-zinc-300 text-lg font-bold">{monthlyPrice.toFixed(0)} <span className="text-xs text-zinc-500 font-normal">CHF/mois</span></div>
                            <div className="text-zinc-500 text-[10px]">{item.months || 6} mois · total {totalEngagement.toFixed(0)} CHF</div>
                          </button>
                          <button
                            type="button" onClick={() => setBillingCycle('annual')}
                            className={`p-3 rounded-xl border-2 transition-all text-left relative ${billingCycle === 'annual' ? 'border-green-400 bg-green-500/15' : 'border-white/10 bg-white/5 hover:border-white/30'}`}
                            data-testid="cycle-annual"
                          >
                            <span className="absolute top-1 right-1 px-1.5 py-0.5 bg-green-500 text-white text-[9px] font-bold rounded-full">-5%</span>
                            <div className="text-white font-semibold text-sm">Paiement intégral</div>
                            <div className="text-green-400 text-lg font-bold">{(totalEngagement * 0.95).toFixed(0)} <span className="text-xs text-zinc-500 font-normal">CHF</span></div>
                            <div className="text-zinc-500 text-[10px]">Économie {(totalEngagement * 0.05).toFixed(0)} CHF</div>
                          </button>
                        </div>
                      </div>
                    )}

                    <div>
                      <p className="text-zinc-400 text-xs uppercase tracking-wider mb-2">Mode de paiement</p>
                      <div className="space-y-2.5">
                        {/* Option Stripe */}
                        <button
                          type="button"
                          onClick={() => { setMethod('stripe'); setStep(2); }}
                          className="w-full p-4 bg-white/5 hover:bg-white/10 border-2 border-white/10 hover:border-primary rounded-2xl transition-all flex items-center gap-3 text-left group"
                          data-testid="method-stripe"
                        >
                          <div className="p-2.5 bg-gradient-to-br from-primary to-pink-500 rounded-xl group-hover:scale-110 transition-transform">
                            <CreditCard className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="text-white font-semibold flex items-center gap-2">
                              Carte bancaire
                              <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-bold ${cardFeeExempt ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}`} data-testid="stripe-fee-badge">
                                {cardFeeExempt ? '0% frais' : '+3% frais'}
                              </span>
                            </div>
                            <div className="text-zinc-400 text-xs">Stripe sécurisé{installmentOptions.length > 1 ? ` · 1x, 2x ou 3x` : ' · 1x'}</div>
                          </div>
                        </button>

                        {/* Option Virement */}
                        <button
                          type="button"
                          onClick={() => { setMethod('bank_transfer'); setInstallments(1); setStep(2); }}
                          className="w-full p-4 bg-white/5 hover:bg-white/10 border-2 border-white/10 hover:border-blue-400 rounded-2xl transition-all flex items-center gap-3 text-left group"
                          data-testid="method-bank"
                        >
                          <div className="p-2.5 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl group-hover:scale-110 transition-transform">
                            <FileText className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="text-white font-semibold flex items-center gap-2">
                              Virement bancaire
                              <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-emerald-500/20 text-emerald-300" data-testid="bank-fee-badge">0% frais</span>
                            </div>
                            <div className="text-zinc-400 text-xs">Facture PDF avec QR-bill suisse · 1x uniquement</div>
                          </div>
                        </button>
                      </div>

                      {!cardFeeExempt && (
                        <div className="mt-3 flex items-start gap-2.5 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20" data-testid="bank-transfer-savings-tip">
                          <PiggyBank className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                          <p className="text-emerald-300 text-xs leading-relaxed">
                            Le virement bancaire est toujours à <strong>0% de frais</strong> — économisez jusqu'à 3 à 5% par rapport au paiement par carte.
                          </p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* === STEP 2 : Détails du paiement === */}
                {step === 2 && (
                  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5">
                    <button onClick={() => setStep(1)} className="text-zinc-400 hover:text-white text-sm flex items-center gap-1.5 transition-colors" data-testid="step2-back">
                      ← Retour
                    </button>

                    {/* Choix des mensualités (Stripe + > 1 option) */}
                    {method === 'stripe' && installmentOptions.length > 1 && (
                      <div data-testid="installment-options">
                        <p className="text-zinc-400 text-xs uppercase tracking-wider mb-2">Choisissez votre fréquence</p>
                        <div className="grid grid-cols-3 gap-2">
                          {installmentOptions.map((n) => {
                            const feeRateN = feesByInstallment(n);
                            const totalN = effectiveBasePriceNum * (1 + feeRateN);
                            const monthlyN = totalN / n;
                            const selected = installments === n;
                            const feeLabel = feeRateN === 0 ? 'Sans frais' : `+${(feeRateN * 100).toFixed(0)}% frais`;
                            return (
                              <button
                                key={n}
                                type="button"
                                onClick={() => setInstallments(n)}
                                className={`relative p-3 rounded-2xl border-2 transition-all ${selected ? 'border-primary bg-primary/15' : 'border-white/10 bg-white/5 hover:border-white/30'}`}
                                data-testid={`installment-${n}x`}
                              >
                                {selected && (
                                  <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                                    <Check className="w-3 h-3 text-white" />
                                  </span>
                                )}
                                <div className="text-zinc-400 text-xs">{n}x</div>
                                <div className="text-white text-2xl font-extrabold leading-tight mt-0.5" data-testid={`installment-${n}x-price`}>
                                  {monthlyN.toFixed(0)}
                                  <span className="text-xs text-zinc-500 font-normal ml-0.5">CHF{n > 1 ? '/mois' : ''}</span>
                                </div>
                                <div className={`text-[10px] mt-1 font-semibold ${feeRateN === 0 ? 'text-green-400' : feeRateN <= 0.03 ? 'text-yellow-400' : 'text-orange-400'}`}>
                                  {feeLabel}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        {installments > 1 && (
                          <div className="mt-3 text-center text-zinc-500 text-xs">
                            Total : <span className="text-zinc-300 font-semibold">{totalWithFees.toFixed(2)} CHF</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Formulaire client */}
                    <div className="space-y-3">
                      <div>
                        <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-1.5">Nom complet *</label>
                        <input
                          type="text" value={customerInfo.name}
                          onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                          placeholder="Prénom Nom"
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-zinc-500 focus:border-primary focus:outline-none text-sm"
                          data-testid="customer-name-input"
                        />
                      </div>
                      <div>
                        <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-1.5">Email *</label>
                        <input
                          type="email" value={customerInfo.email}
                          onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                          placeholder="votre@email.com"
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-zinc-500 focus:border-primary focus:outline-none text-sm"
                          data-testid="customer-email-input"
                        />
                      </div>
                      {method === 'bank_transfer' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-1.5">Adresse</label>
                            <input
                              type="text" value={customerInfo.address}
                              onChange={(e) => setCustomerInfo({ ...customerInfo, address: e.target.value })}
                              placeholder="Rue et n°"
                              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-zinc-500 focus:border-primary focus:outline-none text-sm"
                              data-testid="customer-address-input"
                            />
                          </div>
                          <div>
                            <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-1.5">NPA + Ville</label>
                            <input
                              type="text" value={customerInfo.city}
                              onChange={(e) => setCustomerInfo({ ...customerInfo, city: e.target.value })}
                              placeholder="1260 Nyon"
                              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-zinc-500 focus:border-primary focus:outline-none text-sm"
                              data-testid="customer-city-input"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Code promo (Stripe uniquement) */}
                    {method === 'stripe' && (
                      <div data-testid="promo-code-block">
                        <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-1.5">Code promo (facultatif)</label>
                        <div className="flex gap-2">
                          <input
                            type="text" value={promoCode}
                            onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoApplied(null); setPromoError(''); }}
                            placeholder="EX: BODYUP2026"
                            className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-zinc-500 focus:border-primary focus:outline-none text-sm uppercase"
                            data-testid="promo-code-input"
                          />
                          <button type="button" onClick={checkPromoCode} disabled={promoChecking || !promoCode.trim()}
                            className="px-4 py-2.5 rounded-xl font-bold text-sm bg-white/10 hover:bg-white/20 text-white disabled:opacity-50 transition-colors"
                            data-testid="promo-code-apply-btn">
                            {promoChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Valider'}
                          </button>
                        </div>
                        {promoError && <p className="text-red-400 text-xs mt-1.5" data-testid="promo-code-error">{promoError}</p>}
                        {promoApplied && (
                          <p className="text-emerald-400 text-xs mt-1.5 flex items-center gap-1" data-testid="promo-code-success">
                            <CheckCircle className="w-3.5 h-3.5" /> Code "{promoApplied.code}" appliqué — nouveau prix : {promoApplied.discounted_amount.toFixed(2)} CHF
                          </p>
                        )}
                      </div>
                    )}

                    {error && (
                      <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-2" data-testid="payment-error">
                        <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                        <p className="text-red-300 text-xs">{error}</p>
                      </div>
                    )}

                    {/* CTA principal */}
                    <button
                      onClick={method === 'stripe' ? handleStripePayment : handleBankTransfer}
                      disabled={loading || !customerInfo.name || !customerInfo.email}
                      className="w-full py-4 bg-gradient-to-r from-primary to-pink-500 text-white font-bold rounded-xl transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
                      data-testid="payment-submit"
                    >
                      {loading ? (
                        <><Loader2 className="w-5 h-5 animate-spin" /> Traitement…</>
                      ) : method === 'stripe' ? (
                        <><CreditCard className="w-5 h-5" /> Payer {installments > 1 ? `${installments}x ${monthlyAmount.toFixed(2)}` : monthlyAmount.toFixed(2)} CHF</>
                      ) : (
                        <><FileText className="w-5 h-5" /> Télécharger la facture</>
                      )}
                    </button>

                    <p className="text-zinc-500 text-[11px] text-center">
                      En validant, vous vous engagez sur la durée complète du forfait.
                    </p>
                  </motion.div>
                )}

                {/* === STEP 3 : Succès virement === */}
                {step === 3 && (
                  <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-6 space-y-5" data-testid="bank-success">
                    <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-2">Facture téléchargée !</h3>
                      <p className="text-zinc-400 text-sm">Votre facture avec QR-bill suisse a été téléchargée et envoyée par email.</p>
                    </div>
                    <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl text-left text-xs text-blue-300">
                      📲 Scannez le QR-bill depuis votre app bancaire (Raiffeisen, UBS, PostFinance…) pour effectuer le virement.
                    </div>
                    <button onClick={handleClose} className="px-6 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors text-sm">
                      Fermer
                    </button>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};
